import {useEffect, useRef} from 'react';

import {useFragment} from '../generated/fragment-masking';
import {ViewerAnalytics_UserFragmentDoc} from '../generated/graphql';
import type {FragmentType} from '../generated/fragment-masking';
import {trackChameleonBucket} from './chameleon';
import {initializeTrackingScripts} from './consent/analytics';
import {groupViewer, identifyViewer} from './segment';
import {viewerGroups, viewerTraits} from './viewerTraits';

export type AnalyticsViewer = FragmentType<
  typeof ViewerAnalytics_UserFragmentDoc
>;

/**
 * Inserts the tracking scripts for this session and identifies the viewer.
 * Replaces the wandb remote's `useConsentAwareAnalyticsInjection`, which drove
 * the same injector off the remote's own Redux viewer.
 *
 * Runs at the surface level rather than inside a route, so it covers remote
 * pages too — a remote page is not wrapped in `RequireViewer`, so nothing under
 * it would otherwise ask who the viewer is.
 *
 * `identify` is not awaited against script loading. Segment's snippet queues
 * calls and analytics.js replays them in order on load, so identifying as soon
 * as the viewer is known is enough to guarantee it precedes every `track` a
 * remote sends — and waiting on `initializeTrackingScripts` would mean waiting
 * on a promise that never resolves when OneTrust fails to load.
 *
 * The shell never refetches its `Viewer` query, so in practice traits change
 * only when Apollo's cache is updated from elsewhere. The effect is written to
 * follow the data anyway rather than to fire once.
 */
export function useAnalyticsBootstrap({
  viewer,
  loading,
}: {
  viewer: AnalyticsViewer | null;
  loading: boolean;
}): void {
  const analyticsViewer = useFragment(ViewerAnalytics_UserFragmentDoc, viewer);
  /*
    Whether tracking scripts have been asked for at all. Consent no longer
    depends on who the viewer is, so this is once per page rather than once per
    authentication state.

    The loader dedupes internally too, so this is belt and braces — but the
    effect re-runs on every change to the viewer's traits, not just on sign-in,
    and a consent pass is not something to re-drive on a bio edit.
  */
  const injected = useRef(false);

  useEffect(() => {
    if (loading) {
      return;
    }

    /*
      Independent of the viewer, and idempotent, so it does not need the
      `injected` guard below — it only has to happen once the shell is running
      analytics at all.
    */
    trackChameleonBucket();

    if (!injected.current) {
      injected.current = true;
      void initializeTrackingScripts().catch(error => {
        // Individual vendor failures are already caught and logged by the
        // loader, so reaching here means the consent pass itself broke.
        console.error('Failed to initialize tracking scripts', error);
      });
    }

    if (analyticsViewer == null) {
      return;
    }

    const traits = viewerTraits(analyticsViewer);
    if (traits == null) {
      return;
    }

    /*
      Re-identifies whenever the viewer's data changes, not once per session:
      Apollo hands back a referentially stable object until a field actually
      differs, so this tracks trait edits — a new team, a changed bio — the way
      the app's own `identifyWithViewer` did before the shell took over. Guarding
      on the viewer id instead would freeze `teamCount`, `teams`, `isPaid`,
      `description` and `website` at their first-seen values for the whole
      session.
    */
    identifyViewer(analyticsViewer.id, traits);
    for (const group of viewerGroups(analyticsViewer)) {
      groupViewer(group.name, {
        entityName: group.entityName,
        isPaid: group.isPaid,
      });
    }
  }, [analyticsViewer, loading]);
}
